import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Grid,
  TablePagination,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CompareArrows as CompareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Transaction {
  id: string;
  type: string;
  description: string;
  amount: number | null;
  currency: string | null;
  created_at_revolut: string | null;
}

interface DateComparisonData {
  date: string;
  api_count: number;
  csv_count: number;
  api_transactions: Transaction[];
  csv_transactions: Transaction[];
}


const TransactionComparisonByDate: React.FC = () => {
  const [comparisonData, setComparisonData] = useState<DateComparisonData[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  } | null>(null);
  const [timestamp, setTimestamp] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const fetchComparisonData = useCallback(async (pageNum?: number) => {
    setLoading(true);
    setError(null);

    try {
      const currentPage = pageNum !== undefined ? pageNum : page + 1;
      const response = await api.get('/api/admin/transaction-comparison-by-date', {
        timeout: 60000,  // 60 seconds timeout for initial load with all dates
        params: {
          page: currentPage,
          per_page: rowsPerPage
        }
      });
      setComparisonData(response.data.data);
      setPagination(response.data.pagination);
      setTimestamp(response.data.timestamp);
    } catch (err: any) {
      console.error('Error fetching comparison data:', err);
      setError(err.response?.data?.error || 'Failed to fetch comparison data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (date: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading transaction comparison...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => fetchComparisonData()} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!comparisonData || comparisonData.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No comparison data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
          <CompareIcon sx={{ mr: 2 }} />
          2024 Transaction Comparison by Date
        </Typography>
        <Button
          variant="contained"
          onClick={() => fetchComparisonData()}
          startIcon={<RefreshIcon />}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Main Comparison Table */}
      <Card>
        <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
          <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
            <FitColgroup
              cols={buildFitColgroup([
                { key: 'date', pct: 12 },
                { key: 'api-count', pct: 9 },
                { key: 'csv-count', pct: 9 },
                { key: 'api-sample' },
                { key: 'csv-sample', pct: 30 },
                { key: 'expand', pct: 8 },
              ])}
            />
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  API Count
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  CSV Count
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  API Transactions (Sample)
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  CSV Transactions (Sample)
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonData.map((row) => {
                const isExpanded = expandedRows.has(row.date);
                const countDifference = row.api_count - row.csv_count;
                
                return (
                  <React.Fragment key={row.date}>
                    <TableRow hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="body2" 
                          color="primary"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {row.api_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="body2" 
                          color="secondary"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {row.csv_count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {row.api_transactions.slice(0, 2).map((tx: Transaction, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Chip label={tx.type} size="small" />
                              <Typography variant="body2" sx={{ ...fitTruncateSx, fontSize: '0.75rem' }} title={tx.description || 'N/A'}>
                                {tx.description || 'N/A'}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={tx.amount && tx.amount < 0 ? 'error.main' : 'success.main'}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {formatAmount(tx.amount)}
                              </Typography>
                            </Box>
                          ))}
                          {row.api_transactions.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{row.api_transactions.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {row.csv_transactions.slice(0, 2).map((tx: Transaction, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Chip label={tx.type} size="small" color="secondary" />
                              <Typography variant="body2" sx={{ ...fitTruncateSx, fontSize: '0.75rem' }} title={tx.description || 'N/A'}>
                                {tx.description || 'N/A'}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={tx.amount && tx.amount < 0 ? 'error.main' : 'success.main'}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {formatAmount(tx.amount)}
                              </Typography>
                            </Box>
                          ))}
                          {row.csv_transactions.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{row.csv_transactions.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="fit-table-no-clip">
                        <Button
                          size="small"
                          onClick={() => toggleRowExpansion(row.date)}
                          startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {isExpanded ? 'Less' : 'More'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row Details */}
                    <TableRow>
                      <TableCell colSpan={6} className="fit-table-no-clip" sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  All API Transactions ({row.api_count})
                                </Typography>
                                <Table size="small" sx={FIT_TABLE_SX}>
                                  <FitColgroup
                                    cols={buildFitColgroup([
                                      { key: 'time', pct: 16 },
                                      { key: 'type', pct: 20 },
                                      { key: 'description' },
                                      { key: 'amount', pct: 20 },
                                    ])}
                                  />
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Time</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell align="right">Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.api_transactions.map((tx: Transaction, idx: number) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {formatTime(tx.created_at_revolut)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={tx.type} size="small" />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" sx={fitTruncateSx} title={tx.description || 'N/A'}>
                                            {tx.description || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography 
                                            variant="body2" 
                                            color={tx.amount && tx.amount < 0 ? 'error.main' : 'success.main'}
                                          >
                                            {formatAmount(tx.amount)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Grid>
                              
                              <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" color="secondary" gutterBottom>
                                  All CSV Transactions ({row.csv_count})
                                </Typography>
                                <Table size="small" sx={FIT_TABLE_SX}>
                                  <FitColgroup
                                    cols={buildFitColgroup([
                                      { key: 'time', pct: 16 },
                                      { key: 'type', pct: 20 },
                                      { key: 'description' },
                                      { key: 'amount', pct: 20 },
                                    ])}
                                  />
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Time</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell align="right">Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.csv_transactions.map((tx: Transaction, idx: number) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {formatTime(tx.created_at_revolut)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={tx.type} size="small" color="secondary" />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" sx={fitTruncateSx} title={tx.description || 'N/A'}>
                                            {tx.description || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography 
                                            variant="body2" 
                                            color={tx.amount && tx.amount < 0 ? 'error.main' : 'success.main'}
                                          >
                                            {formatAmount(tx.amount)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Grid>
                            </Grid>
                            
                            {/* Difference Alert */}
                            {countDifference !== 0 && (
                              <Alert 
                                severity={countDifference > 0 ? 'warning' : 'info'} 
                                sx={{ mt: 2 }}
                              >
                                {countDifference > 0 
                                  ? `API has ${countDifference} more transaction(s) than CSV for this date.`
                                  : `CSV has ${Math.abs(countDifference)} more transaction(s) than API for this date.`
                                }
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={pagination?.total_count || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      
      {/* Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Last updated: {formatDate(timestamp)}
      </Typography>
    </Box>
  );
};

export default TransactionComparisonByDate;
